import ReactMarkdown from 'react-markdown'
import { memo } from 'react'
import type { ReactNode } from 'react'

interface MarkdownRendererProps {
  readonly content: string
  readonly className?: string
}

interface HeadingProps {
  children?: ReactNode
  [key: string]: any
}

interface CodeProps {
  inline?: boolean
  children?: ReactNode
  [key: string]: any
}

// Heading components - moved outside parent component
function MarkdownH1({ children, ...props }: HeadingProps) {
  // Only render if heading has content
  if (!children || (typeof children === 'string' && !children.trim())) {
    return null
  }
  return <h1 className="text-xl font-bold mt-4 mb-2" {...props}>{children}</h1>
}

function MarkdownH2({ children, ...props }: HeadingProps) {
  if (!children || (typeof children === 'string' && !children.trim())) {
    return null
  }
  return <h2 className="text-lg font-bold mt-3 mb-2" {...props}>{children}</h2>
}

function MarkdownH3({ children, ...props }: HeadingProps) {
  if (!children || (typeof children === 'string' && !children.trim())) {
    return null
  }
  return <h3 className="text-base font-semibold mt-2 mb-1" {...props}>{children}</h3>
}

function MarkdownH4({ children, ...props }: HeadingProps) {
  if (!children || (typeof children === 'string' && !children.trim())) {
    return null
  }
  return <h4 className="text-sm font-semibold mt-2 mb-1" {...props}>{children}</h4>
}

function MarkdownH5({ children, ...props }: HeadingProps) {
  if (!children || (typeof children === 'string' && !children.trim())) {
    return null
  }
  return <h5 className="text-xs font-semibold mt-1 mb-1" {...props}>{children}</h5>
}

function MarkdownH6({ children, ...props }: HeadingProps) {
  if (!children || (typeof children === 'string' && !children.trim())) {
    return null
  }
  return <h6 className="text-xs font-semibold mt-1 mb-1" {...props}>{children}</h6>
}

// Other markdown components - moved outside parent component
function MarkdownStrong({ ...props }: { [key: string]: any }) {
  return <strong className="font-semibold" {...props} />
}

function MarkdownUl({ ...props }: { [key: string]: any }) {
  return <ul className="list-disc list-inside my-2 space-y-1" {...props} />
}

function MarkdownOl({ ...props }: { [key: string]: any }) {
  return <ol className="list-decimal list-inside my-2 space-y-1" {...props} />
}

function MarkdownLi({ ...props }: { [key: string]: any }) {
  return <li className="ml-2" {...props} />
}

function MarkdownP({ ...props }: { [key: string]: any }) {
  return <p className="my-1" {...props} />
}

function MarkdownCode({ inline, ...props }: CodeProps) {
  return inline ? (
    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm" {...props} />
  ) : (
    <code className="block bg-gray-100 p-2 rounded my-2 text-sm overflow-x-auto" {...props} />
  )
}

// Markdown components configuration - defined outside component
const markdownComponents = {
  h1: MarkdownH1,
  h2: MarkdownH2,
  h3: MarkdownH3,
  h4: MarkdownH4,
  h5: MarkdownH5,
  h6: MarkdownH6,
  strong: MarkdownStrong,
  ul: MarkdownUl,
  ol: MarkdownOl,
  li: MarkdownLi,
  p: MarkdownP,
  code: MarkdownCode,
}

function MarkdownRendererInner({ content, className }: Readonly<MarkdownRendererProps>) {
  return (
    <div className={className}>
      <ReactMarkdown components={markdownComponents}>
        {content}
      </ReactMarkdown>
    </div>
  )
}

export const MarkdownRenderer = memo(MarkdownRendererInner)
